import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  PlusIcon, 
  DocumentTextIcon,
  ClipboardDocumentIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  FolderIcon,
  UserIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';

import { Button, Card, Spinner, Badge } from '@realvisor/ui';
import { useForms, useDeleteForm, useWidgetLeads, type Form, type WidgetLead } from '@/hooks';
import { useProject } from '@/contexts/ProjectContext';
import ApiAccessSection from './ApiAccessSection';

function CopyButton({ text, label }: { text: string; label: string }) {
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} zkopírován do schránky`);
  };

  return (
    <button
      onClick={handleCopy}
      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
      title={`Kopírovat ${label}`}
    >
      <ClipboardDocumentIcon className="h-4 w-4" />
    </button>
  );
}

export default function Forms() {
  const { currentProject, isAllProjects, projects, switchProject } = useProject();
  const { data: forms, isLoading } = useForms(currentProject?.id);
  const { data: widgetLeadsData, isLoading: widgetLeadsLoading } = useWidgetLeads(currentProject?.id);
  const deleteForm = useDeleteForm();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (form: Form) => {
    if (!confirm(`Opravdu chcete smazat formulář "${form.name}"?`)) return;
    
    setDeletingId(form.id);
    try {
      await deleteForm.mutateAsync(form.id);
      toast.success('Formulář smazán');
    } catch (error) {
      toast.error('Nepodařilo se smazat formulář');
    } finally {
      setDeletingId(null);
    }
  };

  const getEmbedCode = (form: Form) => {
    const widgetUrl = import.meta.env['VITE_WIDGET_URL'] || 'https://realvisor-widget.vercel.app';
    const apiUrl = import.meta.env['VITE_API_URL'] || 'https://api-production-88cf.up.railway.app';
    return `<div id="realvisor-form"></div>
<script src="${widgetUrl}/widget.iife.js"></script>
<script>
  RealvisorWidget.createValuoForm(document.getElementById('realvisor-form'), {
    apiUrl: '${apiUrl}/api/v1',
    formCode: '${form.code}',
    projectId: '${form.projectId}'
  });
</script>`;
  };

  // Zobrazit výzvu k výběru projektu pokud je vybráno "Všechny projekty"
  if (isAllProjects) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Formuláře</h1>
            <p className="mt-1 text-sm text-gray-500">
              Spravujte kontaktní formuláře pro sběr leadů
            </p>
          </div>
        </div>

        <Card padding="lg">
          <div className="text-center py-8">
            <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">Vyberte projekt</h3>
            <p className="mt-2 text-sm text-gray-500">
              Pro správu formulářů vyberte konkrétní projekt v sidebaru.
            </p>
            {projects.length > 0 && (
              <div className="mt-6">
                <select
                  value=""
                  onChange={(e) => switchProject(e.target.value)}
                  className="block w-full max-w-xs mx-auto rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="" disabled>Vyberte projekt...</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Formuláře</h1>
          <p className="mt-1 text-sm text-gray-500">
            Spravujte kontaktní formuláře pro sběr leadů
          </p>
        </div>
        <Link to="/forms/new">
          <Button leftIcon={<PlusIcon className="h-5 w-5" />}>
            Nový formulář
          </Button>
        </Link>
      </div>

      {forms && forms.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {forms.map((form) => (
            <Card key={form.id} padding="md" className="relative">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <DocumentTextIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{form.name}</h3>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <code className="bg-gray-100 px-1 rounded">{form.code}</code>
                      <CopyButton text={form.code} label="Kód" />
                    </div>
                  </div>
                </div>
                <Badge variant={form.isActive ? 'success' : 'default'}>
                  {form.isActive ? 'Aktivní' : 'Neaktivní'}
                </Badge>
              </div>

              <div className="mt-4 space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Projekt:</span>
                  <span className="font-medium">{form.project?.name || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Pipeline:</span>
                  <span className="font-medium">{form.targetPipeline?.name || 'Výchozí'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Submissions:</span>
                  <span className="font-medium">{form._count?.submissions || 0}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500">Embed:</span>
                    <CopyButton text={getEmbedCode(form)} label="Embed kód" />
                  </div>
                  <div className="flex items-center gap-1">
                    <Link to={`/forms/${form.id}/preview`}>
                      <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                        <EyeIcon className="h-4 w-4" />
                      </button>
                    </Link>
                    <Link to={`/forms/${form.id}`}>
                      <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </Link>
                    <button 
                      onClick={() => handleDelete(form)}
                      disabled={deletingId === form.id}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card padding="lg">
          <div className="text-center py-8">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-sm font-medium text-gray-900">Žádné formuláře</h3>
            <p className="mt-1 text-sm text-gray-500">
              Vytvořte první formulář pro sběr leadů z vašeho webu.
            </p>
            <div className="mt-6">
              <Link to="/forms/new">
                <Button leftIcon={<PlusIcon className="h-5 w-5" />}>
                  Vytvořit formulář
                </Button>
              </Link>
            </div>
          </div>
        </Card>
      )}

      {/* Widget Forms - Hypotéka a Prescoring */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Leady z widget formulářů</h2>
            <p className="mt-1 text-sm text-gray-500">
              Hypotéka a Prescoring formuláře
            </p>
          </div>
        </div>

        {widgetLeadsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : widgetLeadsData && widgetLeadsData.data.length > 0 ? (
          <div className="space-y-3">
            {widgetLeadsData.data.map((lead) => {
              const formType = lead.sourcePayload?.['formType'];
              const formTypeLabel = formType === 'hypokalkulacka' ? 'Hypotéka' : formType === 'prescoring' ? 'Prescoring' : 'Widget';
              const formTypeColor = formType === 'hypokalkulacka' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700';
              
              return (
                <Link key={lead.id} to={`/leads/${lead.id}`}>
                  <Card padding="md" className="hover:shadow-md transition-shadow cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                          <UserIcon className="h-5 w-5" />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-gray-900">
                              {lead.contact.firstName} {lead.contact.lastName || ''}
                            </h3>
                            <Badge variant="default" className={formTypeColor}>
                              {formTypeLabel}
                            </Badge>
                          </div>
                          
                          <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                            {lead.contact.email && (
                              <span>{lead.contact.email}</span>
                            )}
                            {lead.contact.phone && (
                              <span>{lead.contact.phone}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900">
                            {lead.project.name}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                            <CalendarIcon className="h-3 w-3" />
                            {lead.createdAt ? (() => {
                              try {
                                const date = new Date(lead.createdAt);
                                return !isNaN(date.getTime()) ? format(date, 'd. M. yyyy HH:mm', { locale: cs }) : '-';
                              } catch {
                                return '-';
                              }
                            })() : '-'}
                          </div>
                        </div>
                        
                        <Badge 
                          variant="default" 
                          style={{ 
                            backgroundColor: `${lead.currentStage.color}20`,
                            color: lead.currentStage.color,
                            borderColor: `${lead.currentStage.color}40`
                          }}
                        >
                          {lead.currentStage.name}
                        </Badge>
                      </div>
                    </div>

                    {lead.notes && (
                      <div className="mt-3 text-sm text-gray-600 line-clamp-2">
                        {lead.notes}
                      </div>
                    )}
                  </Card>
                </Link>
              );
            })}
          </div>
        ) : (
          <Card padding="lg">
            <div className="text-center py-8">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-sm font-medium text-gray-900">Žádné leady z widget formulářů</h3>
              <p className="mt-1 text-sm text-gray-500">
                Zatím nebyly vytvořeny žádné leady z Hypotéky nebo Prescoringu.
              </p>
            </div>
          </Card>
        )}
      </div>

      {/* API přístup pro projekt */}
      {currentProject && (
        <div className="mt-8">
          <ApiAccessSection projectId={currentProject.id} />
        </div>
      )}
    </div>
  );
}
